/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ComponentType, useCallback, useEffect, useMemo, useState } from 'react';

import { FormattedMessage } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { Text } from '@atlaskit/primitives/compiled/text';
import { useFeatureExposed } from '@atlassian/feature-exposed/hook';

import type {
	InvitedAppShortcutAllowedApsKey,
	InvitedAppShortcutAllowedTargetAppKey,
	InvitedAppShortcutVariant,
} from '../../../../../common/types';
import { messages } from '../../../../../common/ui/invited-app-shortcuts/messages';
import { getAppKeyByApsKey } from '../../../../../common/utils/invited-app-shortcuts-utils';
import { useInvitedAppAnalytics } from '../../../../../services/invited-app-shortcuts/invited-app-analytics';

interface InvitedAppShortcutLabelProps {
	apsKey: InvitedAppShortcutAllowedApsKey;
	label: string;
	/**
	 * Called when the animated lozenge finishes animating, so the caller can warm GRS
	 * to the static variant for the next load. Only the productionisation container
	 * supplies it; the legacy cohort has no GRS entry to refresh — prop presence, not
	 * `variant`, is what scopes the refresh.
	 */
	onAnimatedImpressed?: (app: InvitedAppShortcutAllowedTargetAppKey) => void;
	/**
	 * Server-resolved lozenge treatment; authoritative when supplied.
	 *
	 * Only the productionisation inner passes this. Absent means "use the legacy
	 * heuristic", not `'none'`: the legacy inner's `resolveFromCache` hardcodes
	 * `'none'`, so treating absent as `'none'` would drop its lozenge.
	 */
	variant?: InvitedAppShortcutVariant;
}

const styles = cssMap({
	container: {
		display: 'grid',
		gridTemplateColumns: 'auto 1fr',
		alignItems: 'center',
		width: '100%',
	},
	lozengeContainer: {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'center',
		minWidth: '0px',
	},
});

const INITIAL_ANIMATION_DELAY = 2000;
const LOOM_INVITED_LOZENGE_EXPERIMENT = 'twcg-2071-twc-loom-invited-lozenge';
const LOOM_INVITED_LOZENGE_METADATA = { experimentName: LOOM_INVITED_LOZENGE_EXPERIMENT };

// apsKeys that have already played their initial animation. Module-scoped so it survives the
// frequent subtree remounts (a `useRef` would be discarded); keyed by apsKey so products stay
// independent; not React state so flipping it can't re-render and cut the animation short.
const impressedApsKeys = new Set<InvitedAppShortcutAllowedApsKey>();

export const InvitedAppShortcutLabel = ({
	label,
	apsKey,
	variant,
	onAnimatedImpressed,
}: InvitedAppShortcutLabelProps): JSX.Element => {
	const [AnimatedLozengeComponent, setAnimatedLozengeComponent] =
		useState<ComponentType<any> | null>(null);
	const { fireViewedAnimatedLozenge } = useInvitedAppAnalytics();

	// A stale `animated` (already played this session) degrades to static.
	const hasAlreadyAnimated = impressedApsKeys.has(apsKey);

	const resolveTreatment = (): InvitedAppShortcutVariant => {
		// No variant supplied: first sighting animates, thereafter static.
		if (variant === undefined) {
			return hasAlreadyAnimated ? 'static' : 'animated';
		}

		switch (variant) {
			case 'animated':
				return hasAlreadyAnimated ? 'static' : 'animated';
			case 'static':
				return 'static';
			// Keeps an unrecognised future variant from animating.
			default:
				return 'none';
		}
	};

	const treatment = resolveTreatment();
	const wantsAnimated = treatment === 'animated';

	useEffect(() => {
		// `static` and `none` skip the chunk, so they paint synchronously.
		if (!wantsAnimated) {
			return;
		}
		import(
			/* webpackChunkName: "@atlassian/invited-app-shortcut-animated-lozenge" */ './animated-lozenge/main'
		).then((module) => {
			// Stored via an updater: a bare `setState(fn)` would be treated as an updater
			// function and called with the previous state. Today's default is a `memo()`
			// object so it happens to survive, but a plain function component would not.
			setAnimatedLozengeComponent(() => module.default as ComponentType<any>);
		});
	}, [wantsAnimated]);

	const isRenderingAnimated = wantsAnimated && AnimatedLozengeComponent !== null;

	// The two signals fire at deliberately different moments, and the gap between them is
	// the point.
	//
	// The event feeds the TAP first-impression trait; the refresh asks GRS to re-read that
	// trait. Firing both together left GRS reading a trait that could not have propagated
	// yet, so it recomputed `NOT_IMPRESSED` and the animation replayed. Firing the event as
	// soon as the animated lozenge renders and the refresh only on completion gives the
	// trait the whole animation (~3.6s) to land, and keeps the event cheap to deliver for
	// users who leave early — a session too short to send the refresh still records the
	// impression, so the next load can converge.
	useEffect(() => {
		if (!isRenderingAnimated) {
			return;
		}
		fireViewedAnimatedLozenge({ app: getAppKeyByApsKey(apsKey) });
	}, [isRenderingAnimated, fireViewedAnimatedLozenge, apsKey]);

	const handleAnimationComplete = useCallback(() => {
		// Tied to completion, not render: a user who leaves mid-animation should get a
		// fresh animation next time rather than being suppressed to static.
		impressedApsKeys.add(apsKey);
		// Safe to re-run: the refresher dedupes per app, so a replayed animation after a
		// remount costs nothing.
		onAnimatedImpressed?.(getAppKeyByApsKey(apsKey));
	}, [apsKey, onAnimatedImpressed]);

	const LozengeComponent =
		wantsAnimated && AnimatedLozengeComponent ? AnimatedLozengeComponent : Lozenge;
	const lozengeProps =
		LozengeComponent === AnimatedLozengeComponent
			? {
					initialAnimationDelay: INITIAL_ANIMATION_DELAY,
					onAnimationComplete: handleAnimationComplete,
				}
			: {};

	const shouldRenderLozenge =
		treatment === 'static' || (wantsAnimated && AnimatedLozengeComponent !== null);
	const isLoomInvitedLozengeRendered = apsKey === 'LOOM' && shouldRenderLozenge;
	const exposureConditions = useMemo(
		() => [isLoomInvitedLozengeRendered],
		[isLoomInvitedLozengeRendered],
	);

	useFeatureExposed(LOOM_INVITED_LOZENGE_EXPERIMENT, 'Test', {
		conditions: exposureConditions,
		metadata: LOOM_INVITED_LOZENGE_METADATA,
	});

	return (
		<div css={styles.container}>
			<Text>{label}</Text>
			<div css={styles.lozengeContainer}>
				{shouldRenderLozenge ? (
					<LozengeComponent appearance="inprogress" {...lozengeProps}>
						<FormattedMessage {...messages.invitedLozenge} />
					</LozengeComponent>
				) : null}
			</div>
		</div>
	);
};

/**
 * Test-only reset for the module-scoped guard.
 */
export const clearInvitedAppShortcutsImpressedGuard = (): void => {
	impressedApsKeys.clear();
};
